import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { siteInfo } from '../data/mock';
import { Search, Menu, X, Phone, Mail, ChevronDown, Download, Package, FileText, Folder, Facebook, Instagram, Tent, Armchair, Wrench, UtensilsCrossed, PartyPopper, Gift, BookOpen } from 'lucide-react';
import { useSearch } from '../context/SearchContext';
import CatalogueModal from './CatalogueModal';
import MobileCatalogueMenu from './MobileCatalogueMenu';
import DesktopSidebar from './DesktopSidebar';
import CatalogueDropdown from './CatalogueDropdown';
import { useSiteSettings } from '../hooks/useSiteSettings';

const catalogueCategories = [
  { 
    id: "barnums-tentes", name: "Barnum et Tentes de Réception", slug: "barnums-tentes", icon: Tent,
    subcategories: [
      { name: "Chapiteaux", slug: "chapiteaux" },
      { name: "Tentes Pliantes raccordables", slug: "tentes-pliantes" },
      { name: "Plancher de Réception", slug: "parquets" },
    ]
  },
  { 
    id: "mobilier-deco", name: "Mobilier et Vaisselle", slug: "mobilier-deco", icon: Armchair,
    subcategories: [
      { name: "Chaises et bancs", slug: "chaises" },
      { name: "Tables et cocktails", slug: "tables" },
      { name: "Vaisselle", slug: "vaisselle" },
      { name: "Décoration", slug: "decoration" },
    ]
  },
  { 
    id: "materiel-accessoires", name: "Matériel et Accessoires", slug: "materiel-accessoires", icon: Wrench,
    subcategories: [
      { name: "Chauffage", slug: "chauffage" },
      { name: "Éclairage", slug: "eclairage" },
      { name: "Sons et Ambiance", slug: "sonorisation" },
      { name: "Groupe électrogène", slug: "remorques-frigo" },
    ]
  },
  { 
    id: "gonflables-jeux", name: "Gonflables et Jeux", slug: "gonflables-jeux", icon: PartyPopper,
    subcategories: [
      { name: "Grandes Structures", slug: "grandes-structures" },
      { name: "Petits Châteaux", slug: "petits-chateaux" },
      { name: "Accessoires & Jeux", slug: "jeux" },
    ]
  },
  { 
    id: "restauration", name: "Restauration et Buvette", slug: "restauration", icon: UtensilsCrossed,
    subcategories: [
      { name: "Animations Gourmandes", slug: "animations-gourmandes" },
      { name: "Tireuses à Bière", slug: "tireuses-biere" },
      { name: "Remorques Frigo", slug: "remorques-frigo" },
    ]
  },
  { 
    id: "pack-tout-inclus", name: "PACK TOUT INCLUS", slug: "pack-tout-inclus", highlight: true, icon: Gift,
    subcategories: [
      { name: "Nos Packs", slug: "packs" },
    ]
  }
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [catalogueOpen, setCatalogueOpen] = useState(false);
  const [mobileCatalogueOpen, setMobileCatalogueOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showCatalogueModal, setShowCatalogueModal] = useState(false);
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarProducts, setSidebarProducts] = useState([]);
  const [sidebarExpanded, setSidebarExpanded] = useState('');
  const settings = useSiteSettings();

  useEffect(function() {
    if ((sidebarOpen || mobileCatalogueOpen) && sidebarProducts.length === 0) {
      fetch(process.env.REACT_APP_BACKEND_URL + '/api/products')
        .then(function(r) { return r.json(); })
        .then(function(data) { setSidebarProducts(data); })
        .catch(function() {});
    }
  }, [sidebarOpen, mobileCatalogueOpen, sidebarProducts.length]);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const mobileSearchRef = useRef(null);
  
  const { searchQuery, searchResults, performSearch, clearSearch } = useSearch();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setCatalogueOpen(false);
    setMobileCatalogueOpen(false);
    setShowResults(false);
    setMobileSearchOpen(false);
    setMobileSearchQuery('');
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    performSearch(query);
    setShowResults(query.length >= 2);
  };

  const handleMobileSearch = (e) => {
    setMobileSearchQuery(e.target.value);
    performSearch(e.target.value);
  };

  const handleMobileHeaderSearch = (e) => {
    if (e.key === 'Enter' && mobileSearchQuery.trim()) {
      navigate(`/catalogue`);
      setMobileSearchOpen(false);
    }
  };

  const handleResultClick = (slug) => {
    navigate(slug);
    clearSearch();
    setShowResults(false);
    setMobileMenuOpen(false);
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'category': return <Folder className="w-4 h-4 text-orange-500" />;
      case 'product': return <Package className="w-4 h-4 text-green-500" />;
      case 'page': return <FileText className="w-4 h-4 text-blue-500" />;
      default: return null;
    }
  };

  return (
    <>
      {/* Top Bar - condensed on mobile */}
      <div className="bg-gray-800 text-white py-1.5 md:py-2 text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-6 flex-wrap">
            <a href="tel:0698430992" className="flex items-center gap-1 hover:text-orange-400 transition-colors text-[11px] sm:text-sm">
              <Phone className="w-3 h-3" />
              <span>Mehdi - 06 98 43 09 92</span>
            </a>
            <a href="tel:0623893298" className="flex items-center gap-1 hover:text-orange-400 transition-colors text-[11px] sm:text-sm">
              <Phone className="w-3 h-3" />
              <span>Damien - 06 23 89 32 98</span>
            </a>
            <a href="mailto:contact@barnum-evenement.fr" className="hidden md:flex items-center gap-2 hover:text-orange-400 transition-colors">
              <Mail className="w-4 h-4" />
              contact@barnum-evenement.fr
            </a>
          </div>
          <div className="flex items-center gap-3">
            <a href="https://www.facebook.com/share/1EDXJ5mPLi/" target="_blank" rel="noopener noreferrer" className="hover:text-orange-400 transition-colors"><Facebook className="w-4 h-4" /></a>
            <a href="https://www.instagram.com/barnum_evenement?stkn=czR4MmVlMmtlbW9p" target="_blank" rel="noopener noreferrer" className="hover:text-orange-400 transition-colors"><Instagram className="w-4 h-4" /></a>
          </div>
        </div>
      </div>

      {/* Ticker - hidden on small mobile */}
      <div className="bg-gray-900 text-orange-300 py-1 overflow-hidden hidden sm:block">
        <div className="ticker-wrapper">
          <div className="ticker-content animate-ticker">
            <span className="mx-8">📍 2 points de contact : Gainneville et Cauville</span>
            <span className="mx-8">•</span>
            <span className="mx-8">Tous nos barnums possèdent des contours amovibles selon la météo</span>
            <span className="mx-8">•</span>
            <span className="mx-8">📍 2 points de contact : Gainneville et Cauville</span>
            <span className="mx-8">•</span>
            <span className="mx-8">Tous nos barnums possèdent des contours amovibles selon la météo</span>
            <span className="mx-8">•</span>
            <span className="mx-8">📍 2 points de contact : Gainneville et Cauville</span>
            <span className="mx-8">•</span>
            <span className="mx-8">Tous nos barnums possèdent des contours amovibles selon la météo</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-24">
            {/* Logo + Hamburger */}
            <div className="flex items-center gap-3">
              {/* Desktop Hamburger for sidebar */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="hidden lg:flex items-center justify-center w-10 h-10 rounded-lg hover:bg-gray-100 transition-colors"
                data-testid="desktop-sidebar-toggle"
              >
                <Menu className="w-5 h-5 text-gray-700" />
              </button>
              <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_tentopia-events/artifacts/6vzdco9s_WhatsApp%20Image%202026-02-27%20at%2018.45.00%20%281%29.jpeg" 
                alt="Barnum Événement" 
                className="h-12 sm:h-14 lg:h-16 w-12 sm:w-14 lg:w-16 rounded-full"
                style={{ objectFit: 'cover', objectPosition: 'center', transform: 'scale(1.15)', clipPath: 'circle(50%)' }}
              />
            </Link>
            </div>
            <nav className="hidden lg:flex items-center gap-8">
              <Link to="/" className={`text-sm font-semibold transition-colors ${location.pathname === '/' ? 'text-orange-600' : 'text-orange-500 hover:text-orange-600'}`}>
                Accueil
              </Link>
              
              <div className="relative">
                <button
                  onClick={() => setCatalogueOpen(!catalogueOpen)}
                  className={`flex items-center gap-1 text-sm font-semibold transition-colors ${location.pathname.includes('/catalogue') ? 'text-orange-600' : 'text-orange-500 hover:text-orange-600'}`}
                >
                  Notre Catalogue
                  <ChevronDown className={`w-4 h-4 transition-transform ${catalogueOpen ? 'rotate-180' : ''}`} />
                </button>
              </div>

              <Link to="/realisations" className={`text-sm font-semibold transition-colors ${location.pathname === '/realisations' ? 'text-orange-600' : 'text-orange-500 hover:text-orange-600'}`}>
                Nos Réalisations
              </Link>

              <Link to="/faq" className={`text-sm font-semibold transition-colors ${location.pathname === '/faq' ? 'text-orange-600' : 'text-orange-500 hover:text-orange-600'}`}>
                FAQ
              </Link>
            </nav>

            {/* Search & CTA */}
            <div className="hidden lg:flex items-center gap-4">
              <div className="relative" ref={searchRef}>
                <div className="flex items-center bg-gray-100 rounded-full px-4 py-2 w-64 hover:bg-gray-200 transition-colors">
                  <Search className="w-5 h-5 text-gray-500 mr-3" />
                  <input
                    type="text"
                    placeholder="Rechercher un produit..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={() => searchQuery.length >= 2 && setShowResults(true)}
                    className="bg-transparent w-full text-sm text-gray-700 placeholder-gray-500 focus:outline-none"
                  />
                </div>
                
                {showResults && searchResults.length > 0 && (
                  <div className="absolute right-0 top-12 w-80 bg-white rounded-xl shadow-xl border border-gray-100 p-4 z-50">
                    <div className="max-h-64 overflow-y-auto">
                      {searchResults.map((result) => (
                        <button
                          key={`${result.type}-${result.slug}-${result.name}`}
                          onClick={() => handleResultClick(result.slug)}
                          className="w-full px-3 py-2 flex items-center gap-3 hover:bg-gray-50 rounded-lg text-left"
                        >
                          {getTypeIcon(result.type)}
                          <span className="text-sm text-gray-700">{result.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link to="/devis" className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-orange-500 rounded-full hover:bg-orange-600 transition-all shadow-md hover:shadow-lg">
                <Mail className="w-4 h-4" />
                Contact et Devis
              </Link>
            </div>

            {/* Mobile buttons */}
            <div className="flex lg:hidden items-center gap-1 flex-1 ml-3">
              <div className="flex items-center bg-gray-100 rounded-full px-3 py-1.5 flex-1" ref={mobileSearchRef}>
                <Search className="w-4 h-4 text-gray-500 mr-2 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={mobileSearchQuery}
                  onChange={handleMobileSearch}
                  onKeyDown={handleMobileHeaderSearch}
                  className="bg-transparent w-full min-w-0 text-sm text-gray-700 placeholder-gray-500 focus:outline-none"
                  data-testid="mobile-header-search-input"
                />
                {mobileSearchQuery && (
                  <button onClick={() => { setMobileSearchQuery(''); clearSearch(); }} className="p-1 text-gray-400">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
              {mobileSearchQuery.length >= 2 && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 bg-white shadow-lg border-t border-gray-100 z-50 max-h-64 overflow-y-auto">
                  {searchResults.slice(0, 5).map((result) => (
                    <button key={`mobile-${result.type}-${result.slug}-${result.name}`} onClick={() => handleResultClick(result.slug)}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 text-left border-b border-gray-50">
                      {getTypeIcon(result.type)}
                      <span className="text-sm text-gray-700">{result.name}</span>
                    </button>
                  ))}
                </div>
              )}
              <Link to="/devis" className="p-2 text-orange-500 flex-shrink-0">
                <Mail className="w-5 h-5" />
              </Link>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-gray-600 hover:text-orange-500 flex-shrink-0"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Catalogue Dropdown - Desktop Mega Menu with emojis + accordéon */}
        <CatalogueDropdown isOpen={catalogueOpen} onClose={function() { setCatalogueOpen(false); }} />

        {/* CHANGE 5: Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 shadow-xl max-h-[80vh] overflow-y-auto">
            {/* Mobile Search Bar */}
            <div className="px-4 py-4 border-b border-gray-100">
              <div className="flex items-center bg-gray-100 rounded-full px-4 py-3">
                <Search className="w-5 h-5 text-gray-500 mr-3" />
                <input
                  type="text"
                  placeholder="Rechercher un produit..."
                  value={mobileSearchQuery}
                  onChange={handleMobileSearch}
                  className="bg-transparent w-full text-sm text-gray-700 placeholder-gray-500 focus:outline-none"
                />
              </div>
              {mobileSearchQuery.length >= 2 && searchResults.length > 0 && (
                <div className="mt-2 bg-gray-50 rounded-lg p-2">
                  {searchResults.slice(0, 5).map((result, index) => (
                    <button
                      key={index}
                      onClick={() => handleResultClick(result.slug)}
                      className="w-full px-3 py-2 flex items-center gap-3 hover:bg-white rounded-lg text-left"
                    >
                      {getTypeIcon(result.type)}
                      <span className="text-sm text-gray-700">{result.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Menu Links */}
            <div className="px-4 py-4 space-y-2">
              <Link to="/" className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg font-medium">
                Accueil
              </Link>
              
              <div>
                <button
                  onClick={() => setMobileCatalogueOpen(!mobileCatalogueOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg font-medium"
                >
                  Notre Catalogue
                  <ChevronDown className={`w-4 h-4 transition-transform ${mobileCatalogueOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileCatalogueOpen && (
                  <MobileCatalogueMenu onClose={function() { setMobileMenuOpen(false); }} />
                )}
              </div>

              <Link to="/realisations" className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg font-medium">
                Nos Réalisations
              </Link>
              
              <Link to="/faq" className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg font-medium">
                FAQ
              </Link>

              <a
                href={settings.catalogueUrlWithoutPrices}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded-lg font-medium"
                data-testid="mobile-feuilleter-catalogue"
              >
                <BookOpen className="w-4 h-4" /> Feuilleter le catalogue
              </a>

              <Link to="/devis" className="block px-4 py-3 mt-4 bg-orange-500 text-white text-center font-semibold rounded-lg">
                Demande de Devis
              </Link>
            </div>
          </div>
        )}
      </header>

      <DesktopSidebar isOpen={sidebarOpen} onClose={function() { setSidebarOpen(false); }} />

      <CatalogueModal isOpen={showCatalogueModal} onClose={() => setShowCatalogueModal(false)} />
    </>
  );
};

export default Header;
